import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import {
  BEFORE,
  CLOSING,
  HERO,
  PHASES,
  PHASES_INTRO,
  RULES,
  SECTIONS,
  TIPS,
  WHAT,
  WHEN,
  type PhaseKey,
} from './guidelinesContent';
import './GuidelinesPage.scss';

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const GuidelinesPage: React.FC = () => {
  const [activePhase, setActivePhase] = useState<PhaseKey>('initiation');
  const phaseIndex = PHASES.findIndex((p) => p.key === activePhase);
  const phase = PHASES[phaseIndex];

  return (
    <div className="guide">
      <Section theme="dark" spacing="tight" className="guide__hero">
        <Eyebrow>Guidance</Eyebrow>
        <Headline as="h1" size="4xl">
          {HERO.title}
        </Headline>
        <Lead>{HERO.lead}</Lead>
        <nav className="guide__jump" aria-label="On this page">
          {SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              className="guide__jump-link"
              onClick={() => scrollToSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </nav>
      </Section>

      <div id="what" className="guide__anchor">
        <Section theme="light">
          <div className="guide__split">
            <div className="guide__copy">
              <Eyebrow>The basics</Eyebrow>
              <Headline as="h2" size="3xl">
                {WHAT.title}
              </Headline>
              {WHAT.paragraphs.map((text) => (
                <p key={text} className="guide__text">
                  {text}
                </p>
              ))}
            </div>
            <aside className="guide__isnot" aria-label="What a PoC is and is not">
              <div className="guide__isnot-col guide__isnot-col--is">
                <p className="guide__isnot-label">A PoC is</p>
                <ul>
                  {WHAT.is.map((item) => (
                    <li key={item}>
                      <span className="guide__mark guide__mark--yes" aria-hidden />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="guide__isnot-col guide__isnot-col--not">
                <p className="guide__isnot-label">A PoC is not</p>
                <ul>
                  {WHAT.isNot.map((item) => (
                    <li key={item}>
                      <span className="guide__mark guide__mark--no" aria-hidden />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </Section>
      </div>

      <div id="when" className="guide__anchor">
        <Section theme="sand">
          <Eyebrow>Is it the right path?</Eyebrow>
          <Headline as="h2" size="3xl">
            {WHEN.title}
          </Headline>
          <Lead>{WHEN.intro}</Lead>
          <div className="guide__reasons">
            {WHEN.reasons.map((reason) => (
              <div key={reason} className="guide__reason">
                <span className="guide__mark guide__mark--yes" aria-hidden />
                <p>{reason}</p>
              </div>
            ))}
          </div>
          <div className="guide__signposts">
            {WHEN.signposts.map((signpost) => (
              <div key={signpost.title} className="guide__signpost">
                <strong>{signpost.title}</strong>
                <p>{signpost.text}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <div id="before" className="guide__anchor">
        <Section theme="dark" className="guide__before">
          <Eyebrow>Have these ready</Eyebrow>
          <Headline as="h2" size="3xl">
            {BEFORE.title}
          </Headline>
          <Lead>{BEFORE.intro}</Lead>
          <div className="guide__ready">
            {BEFORE.items.map((item, index) => (
              <div key={item.title} className="guide__ready-card">
                <span className="guide__ready-num">{index + 1}</span>
                <strong>{item.title}</strong>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
          <p className="guide__before-outro">{BEFORE.outro}</p>
        </Section>
      </div>

      <div id="phases" className="guide__anchor">
        <Section theme="sand">
          <div className="guide__phases">
            <div className="guide__phases-nav">
              <Eyebrow>How it works</Eyebrow>
              <Headline as="h2" size="3xl">
                {PHASES_INTRO.title}
              </Headline>
              <p className="guide__text">{PHASES_INTRO.intro}</p>
              <ol className="guide__stepper" role="tablist" aria-label="PoC phases">
                {PHASES.map((p, index) => {
                  const state =
                    index < phaseIndex ? 'done' : index === phaseIndex ? 'active' : 'upcoming';
                  return (
                    <li key={p.key}>
                      <button
                        type="button"
                        role="tab"
                        id={`phase-tab-${p.key}`}
                        aria-selected={p.key === activePhase}
                        aria-controls="phase-panel"
                        className={`guide__step guide__step--${state}`}
                        onClick={() => setActivePhase(p.key)}
                      >
                        <span className="guide__step-num">{index + 1}</span>
                        <span className="guide__step-text">
                          <strong>{p.name}</strong>
                          <span>{p.summary}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>

            <div
              id="phase-panel"
              role="tabpanel"
              aria-labelledby={`phase-tab-${phase.key}`}
              className={`guide__phase-card guide__phase-card--${phase.key}`}
            >
              <span className="guide__phase-badge">
                Phase {phaseIndex + 1} of {PHASES.length}
              </span>
              <h3 className="guide__phase-title">{phase.name}</h3>
              <p className="guide__text">{phase.body}</p>

              {phase.checks ? (
                <ul className="guide__checks">
                  {phase.checks.map((check) => (
                    <li key={check.name}>
                      <strong>
                        {check.name}
                        {check.mandatory ? (
                          <span className="guide__tag">Mandatory</span>
                        ) : null}
                      </strong>
                      <span>{check.text}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {phase.checksOutro ? (
                <p className="guide__text guide__text--small">{phase.checksOutro}</p>
              ) : null}

              {phase.chips ? (
                <div className="guide__chips">
                  {phase.chips.map((chip) => (
                    <span key={chip} className="guide__chip">
                      {chip}
                    </span>
                  ))}
                </div>
              ) : null}

              {phase.outcomes ? (
                <div className="guide__outcomes">
                  {phase.outcomes.map((outcome) => (
                    <div
                      key={outcome.answer}
                      className={`guide__outcome guide__outcome--${outcome.answer.toLowerCase()}`}
                    >
                      <span className="guide__outcome-answer">If {outcome.answer.toLowerCase()}</span>
                      <p>{outcome.text}</p>
                    </div>
                  ))}
                </div>
              ) : null}

              {phase.note ? <p className="guide__note">{phase.note}</p> : null}

              <div className="guide__phase-footer">
                <button
                  type="button"
                  className="guide__phase-nav"
                  disabled={phaseIndex === 0}
                  onClick={() => setActivePhase(PHASES[phaseIndex - 1].key)}
                >
                  ← Previous
                </button>
                <button
                  type="button"
                  className="guide__phase-nav guide__phase-nav--next"
                  disabled={phaseIndex === PHASES.length - 1}
                  onClick={() => setActivePhase(PHASES[phaseIndex + 1].key)}
                >
                  Next phase →
                </button>
              </div>
            </div>
          </div>
        </Section>
      </div>

      <div id="tips" className="guide__anchor">
        <Section theme="light">
          <Eyebrow>From experience</Eyebrow>
          <Headline as="h2" size="3xl">
            {TIPS.title}
          </Headline>
          <div className="guide__tips">
            {TIPS.items.map((tip, index) => (
              <div key={tip.title} className="guide__tip">
                <span className="guide__tip-num">{String(index + 1).padStart(2, '0')}</span>
                <strong>{tip.title}</strong>
                <p>{tip.text}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <div id="rules" className="guide__anchor">
        <Section theme="dark" className="guide__rules">
          <span className="guide__mandatory">Mandatory</span>
          <Headline as="h2" size="3xl">
            {RULES.title}
          </Headline>
          <Lead>{RULES.intro}</Lead>
          <ol className="guide__rules-list">
            {RULES.items.map((rule, index) => (
              <li key={rule}>
                <span className="guide__rule-num">{index + 1}</span>
                <p>{rule}</p>
              </li>
            ))}
          </ol>
        </Section>
      </div>

      <Section theme="sand" spacing="tight">
        <div className="guide__closing">
          <p>{CLOSING}</p>
          <Link to="/pocs/new" className="guide__cta">
            Start a new PoC →
          </Link>
        </div>
      </Section>
    </div>
  );
};

export default GuidelinesPage;
